/**
 * command 패턴으로 다음 실행에 대한 명령을 로컬 스토리지에 저장.
 * command를 실행할 때, save했던 location일 경우에만 command 실행.
 * 수행이 완료되면 command 삭제.
 */
import {BuyTicketFlowContext} from '../../../domain/buy/ticket/type';
import {Maybe, TicketInfo} from '../../../graphql/generated/types';
import {TRIGGER_COMMAND_KEY} from '../constants';

export async function saveCommand<T extends COMMAND_KEY>({
  commandKey,
  command,
}: {
  commandKey: T;
  command: CommandKeyValueMap[T];
}) {
  window.localStorage.setItem(commandKey, JSON.stringify(command));
}

export const deleteCommand = (commandKey: COMMAND_KEY) => {
  window.localStorage.removeItem(commandKey);
};

export async function runCommand<T extends COMMAND_KEY>({
  commandKey,
  action,
}: {
  commandKey: T;
  action: (commandData: CommandKeyValueMap[T]) => Promise<void>;
}) {
  const searchParams = new URLSearchParams(window.location.search);

  if (searchParams.get(TRIGGER_COMMAND_KEY) !== commandKey) {
    return;
  }

  searchParams.delete(TRIGGER_COMMAND_KEY);
  const searchParamsStr = searchParams.toString();
  const newUrl = `${window.location.pathname}${
    searchParamsStr ? `?${searchParamsStr}` : ''
  }`;
  history.replaceState(null, '', newUrl);

  const _commandData = window.localStorage.getItem(commandKey);
  const commandData = _commandData ? JSON.parse(_commandData) : null;
  if (commandData && window.location.pathname === commandData.location) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
    const {location, ...data} = commandData;
    deleteCommand(commandKey);
    await action(data);
  } else {
    deleteCommand(commandKey);
  }
}

export function useCommandWithLocalStorage() {
  return {
    saveCommand,
    runCommand,
    deleteCommand,
  };
}

export enum COMMAND_KEY {
  /** 콘텐츠홈에서 미로그인 상태로 선물받기 시 로그인 후 선물이 지급되어야합니다. */
  CLICK_CONTENT_HOME_GIFT = 'CLICK_CONTENT_HOME_GIFT',
  /** 콘텐츠홈에서 미로그인 상태로 유료회차 구매시 구매 팝업이 떠야 합니다. */
  CLICK_BUY_TICKETS = 'CLICK_BUY_TICKETS',
  /** 세트상품 구매 페이지에서 미로그인 상태로 구매시 구매 팝업이 떠야 합니다. */
  CLICK_SET_BUY = 'CLICK_SET_BUY',
  /** 뷰어엔드에서 미로그인 상태로 별점주기 시 로그인 후 별점을 남겨야합니다. */
  CLICK_STAR = 'CLICK_STAR',
  /** 뷰어엔드에서 미로그인 상태로 별점주기 시 로그인 후 별점 팝업을 띄워야합니다. */
  CLICK_STAR_POPUP = 'CLICK_STAR_POPUP',
  /** 뷰어엔드에서 미로그인 상태로 별점주기 시 로그인 후 별점 팝업을 띄워야합니다. */
  CLICK_LIKE = 'CLICK_LIKE',
  /** 다음화 진입을 시도할 때 티켓이 부족해서 티켓 페이지로 이동한 후 티켓을 구매해 다음화로 이동한 경우, 이전화로부터 진입했다는 것에 대한 히스토리를 남겨야 합니다. */
  TRIGGER_FROM_PREV_PRODUCT = 'TRIGGER_FROM_PREV_PRODUCT',
  // 캐시환불 시 약관 동의 화면에서 동의 한 금액과 실제 요청 화면에서 보여지는 금액이 다를 경우
  VERIFY_FINAL_REFUNDABLE_AMOUNT = 'VERIFY_FINAL_REFUNDABLE_AMOUNT',
}

type DefaultCommandType = {
  location: string;
};

type CommandKeyValueMap = {
  [COMMAND_KEY.CLICK_CONTENT_HOME_GIFT]: DefaultCommandType & {
    todayGiftUid: number;
  };
  [COMMAND_KEY.CLICK_BUY_TICKETS]: DefaultCommandType &
    BuyTicketFlowContext['ticketProcessParam'] & {
      storedTicketInfo?: TicketInfo;
      expiredConfirmed: Maybe<boolean>;
      currentProductId?: Maybe<number>;
    };
  [COMMAND_KEY.CLICK_SET_BUY]: DefaultCommandType &
    BuyTicketFlowContext['buySetProcessParam'] & {
      storedTicketInfo?: TicketInfo;
    };
  [COMMAND_KEY.CLICK_STAR]: DefaultCommandType & {rating: number};
  [COMMAND_KEY.CLICK_STAR_POPUP]: DefaultCommandType & {};
  [COMMAND_KEY.CLICK_LIKE]: {};
  [COMMAND_KEY.TRIGGER_FROM_PREV_PRODUCT]: DefaultCommandType & {from: number};
  [COMMAND_KEY.VERIFY_FINAL_REFUNDABLE_AMOUNT]: DefaultCommandType & {
    amount: number;
  };
};
